import PrivateLayout from "@/components/private/PrivateLayout";
import { ADMIN_NAV } from "@/components/private/adminNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  computeGlobalScore,
  getZone,
  ZONE_COLORS as zoneColor,
  ZONE_LABELS as zoneLabel,
} from "@shared/scoring";
import { ArrowLeft, Save, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link, useLocation, useRoute } from "wouter";

interface Metric {
  label: string;
  value: string;
  score: number;
  target?: string;
}

interface PillarForm {
  description: string;
  insight: string;
  metrics: Metric[];
}

interface SwingPhase {
  phase: string;
  timing: string;
  quality: number;
  note?: string;
}

interface Priority {
  rank: number;
  pillar: string;
  title: string;
  why: string;
  how: string;
}

const emptyPillar: PillarForm = { description: "", insight: "", metrics: [] };

const DEFAULT_SWING_PHASES: SwingPhase[] = [
  { phase: "Setup", timing: "0%", quality: 70 },
  { phase: "Takeaway", timing: "15%", quality: 70 },
  { phase: "Backswing", timing: "35%", quality: 70 },
  { phase: "Top", timing: "50%", quality: 70 },
  { phase: "Transition", timing: "55%", quality: 70 },
  { phase: "Downswing", timing: "75%", quality: 70 },
  { phase: "Impact", timing: "85%", quality: 70 },
  { phase: "Follow-through", timing: "95%", quality: 70 },
  { phase: "Finish", timing: "100%", quality: 70 },
];

export default function AdminReportEditor() {
  const [, paramsNew] = useRoute<{ playerId: string }>("/admin/jugadores/:playerId/informes/nuevo");
  const [, paramsEdit] = useRoute<{ id: string }>("/admin/informes/:id");
  const [, navigate] = useLocation();

  const editingId = paramsEdit?.id ? Number(paramsEdit.id) : undefined;
  const newPlayerId = paramsNew?.playerId ? Number(paramsNew.playerId) : undefined;

  const { data: editing } = trpc.reports.getByIdAdmin.useQuery(
    { id: editingId! },
    { enabled: !!editingId },
  );
  const { data: playerFromEditing } = trpc.players.getByIdAdmin.useQuery(
    { id: editing?.playerId ?? 0 },
    { enabled: !!editing?.playerId },
  );
  const { data: playerFromNew } = trpc.players.getByIdAdmin.useQuery(
    { id: newPlayerId! },
    { enabled: !!newPlayerId },
  );
  const player = playerFromEditing ?? playerFromNew;

  const createMut = trpc.reports.create.useMutation();
  const updateMut = trpc.reports.update.useMutation();
  const deleteMut = trpc.reports.delete.useMutation();

  const [assessmentDate, setAssessmentDate] = useState(new Date().toISOString().slice(0, 10));
  const [club, setClub] = useState("Driver");
  const [mobility, setMobility] = useState<PillarForm>(emptyPillar);
  const [strength, setStrength] = useState<PillarForm>(emptyPillar);
  const [performance, setPerformance] = useState<PillarForm>(emptyPillar);
  const [motorControl, setMotorControl] = useState<PillarForm>(emptyPillar);
  const [swingSeq, setSwingSeq] = useState<SwingPhase[]>(DEFAULT_SWING_PHASES);
  const [strengths, setStrengths] = useState<string[]>([""]);
  const [alerts, setAlerts] = useState<string[]>([""]);
  const [priorities, setPriorities] = useState<Priority[]>([
    { rank: 1, pillar: "Movilidad", title: "", why: "", how: "" },
  ]);
  const [mainLimiter, setMainLimiter] = useState("");
  const [generalNotes, setGeneralNotes] = useState("");

  useEffect(() => {
    if (!editing) return;
    setAssessmentDate(new Date(editing.assessmentDate).toISOString().slice(0, 10));
    setClub(editing.club || "Driver");
    setMobility((editing.mobilityData as PillarForm) || emptyPillar);
    setStrength((editing.strengthData as PillarForm) || emptyPillar);
    setPerformance((editing.performanceData as PillarForm) || emptyPillar);
    setMotorControl((editing.motorControlData as PillarForm) || emptyPillar);
    setSwingSeq((editing.swingSequenceData as SwingPhase[]) || DEFAULT_SWING_PHASES);
    const edStrengths = (editing.strengths as string[] | null) ?? [];
    const edAlerts = (editing.alerts as string[] | null) ?? [];
    setStrengths(edStrengths.length ? edStrengths : [""]);
    setAlerts(edAlerts.length ? edAlerts : [""]);
    setPriorities((editing.priorities as Priority[]) || []);
    setMainLimiter(editing.mainLimiter || "");
    setGeneralNotes(editing.generalNotes || "");
  }, [editing]);

  const avg = (m: Metric[]) =>
    m.length === 0 ? 0 : Math.round(m.reduce((a, b) => a + (b.score || 0), 0) / m.length);

  const mobilityScore = useMemo(() => avg(mobility.metrics), [mobility]);
  const strengthScore = useMemo(() => avg(strength.metrics), [strength]);
  const performanceScore = useMemo(() => avg(performance.metrics), [performance]);
  const motorControlScore = useMemo(() => avg(motorControl.metrics), [motorControl]);
  const globalScore = useMemo(
    () =>
      computeGlobalScore({
        mobility: mobilityScore,
        strength: strengthScore,
        performance: performanceScore,
        motorControl: motorControlScore,
      }),
    [mobilityScore, strengthScore, performanceScore, motorControlScore],
  );
  const globalZone = getZone(globalScore);

  const save = async (status: "draft" | "published") => {
    if (!player) {
      toast.error("Jugador no disponible");
      return;
    }
    const payload = {
      playerId: player.id,
      assessmentDate,
      club,
      globalScore,
      mobilityScore,
      strengthScore,
      performanceScore,
      motorControlScore,
      mobilityData: mobility,
      strengthData: strength,
      performanceData: performance,
      motorControlData: motorControl,
      swingSequenceData: swingSeq,
      strengths: strengths.filter((s) => s.trim()),
      alerts: alerts.filter((a) => a.trim()),
      priorities,
      mainLimiter,
      generalNotes,
      status,
    };
    try {
      if (editingId) {
        await updateMut.mutateAsync({ id: editingId, data: payload });
        toast.success(status === "published" ? "Informe publicado" : "Borrador guardado");
      } else {
        const res = await createMut.mutateAsync(payload);
        toast.success(status === "published" ? "Informe creado y publicado" : "Borrador creado");
        navigate(`/admin/informes/${res.id}`);
      }
    } catch (e: any) {
      toast.error(e.message || "Error al guardar");
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!confirm("¿Eliminar este informe? No se puede deshacer.")) return;
    try {
      await deleteMut.mutateAsync({ id: editingId });
      toast.success("Informe eliminado");
      navigate(`/admin/jugadores/${player?.id}`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const updateMetric = (pillar: PillarForm, setter: (p: PillarForm) => void) => ({
    add: () =>
      setter({
        ...pillar,
        metrics: [...pillar.metrics, { label: "", value: "", score: 50, target: "" }],
      }),
    remove: (i: number) =>
      setter({ ...pillar, metrics: pillar.metrics.filter((_, idx) => idx !== i) }),
    update: (i: number, patch: Partial<Metric>) =>
      setter({
        ...pillar,
        metrics: pillar.metrics.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
      }),
  });

  const mobilityOps = updateMetric(mobility, setMobility);
  const strengthOps = updateMetric(strength, setStrength);
  const perfOps = updateMetric(performance, setPerformance);
  const motorOps = updateMetric(motorControl, setMotorControl);

  const pillars = [
    { name: "Movilidad", data: mobility, setter: setMobility, ops: mobilityOps, score: mobilityScore },
    { name: "Fuerza", data: strength, setter: setStrength, ops: strengthOps, score: strengthScore },
    { name: "Rendimiento", data: performance, setter: setPerformance, ops: perfOps, score: performanceScore },
    { name: "Control motor", data: motorControl, setter: setMotorControl, ops: motorOps, score: motorControlScore },
  ];

  return (
    <PrivateLayout title="Administración" nav={ADMIN_NAV} requireRole="admin">
      <div className="p-6 md:p-10 max-w-5xl">
        <Link
          href={player ? `/admin/jugadores/${player.id}` : "/admin/jugadores"}
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--forest)] mb-6 hover:underline"
        >
          <ArrowLeft size={14} /> Volver
        </Link>

        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-2">
              {editingId ? "Editar informe" : "Nuevo informe"}
            </div>
            <h1 className="font-display font-light text-[var(--forest-deep)] leading-[1.05]" style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)" }}>
              {player?.fullName || "—"}
            </h1>
            {editing?.code && <p className="text-sm font-mono text-[var(--forest-deep)]/60 mt-1">{editing.code}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] font-semibold tracking-wider uppercase text-[var(--forest-deep)]/60">
                Puntuación global
              </div>
              <div className="font-display text-3xl font-medium" style={{ color: zoneColor[globalZone] }}>
                {globalScore}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: zoneColor[globalZone] }}>
                {zoneLabel[globalZone]}
              </div>
            </div>
          </div>
        </div>

        <Card className="p-6 bg-white border border-[var(--forest-deep)]/10 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fecha valoración</Label>
              <Input type="date" value={assessmentDate} onChange={(e) => setAssessmentDate(e.target.value)} />
            </div>
            <div>
              <Label>Palo valoración</Label>
              <Input value={club} onChange={(e) => setClub(e.target.value)} placeholder="Driver" />
            </div>
            <div className="md:col-span-2">
              <Label>Limitador principal</Label>
              <Input value={mainLimiter} onChange={(e) => setMainLimiter(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Resumen clínico</Label>
              <Textarea rows={3} value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} />
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {pillars.map((p) => {
            const z = getZone(p.score);
            return (
              <div key={p.name} className="p-4 rounded-lg bg-white border border-[var(--forest-deep)]/10 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--forest-deep)]/60 mb-1">
                  {p.name}
                </div>
                <div className="font-display text-2xl font-medium" style={{ color: zoneColor[z] }}>
                  {p.score}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pillars */}
        {pillars.map((p) => (
          <Card key={p.name} className="p-6 bg-white border border-[var(--forest-deep)]/10 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-medium text-[var(--forest-deep)]">{p.name}</h3>
              <Button variant="outline" size="sm" onClick={p.ops.add}>+ Métrica</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label>Descripción</Label>
                <Input
                  value={p.data.description}
                  onChange={(e) => p.setter({ ...p.data, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Lectura clínica (insight)</Label>
                <Input
                  value={p.data.insight}
                  onChange={(e) => p.setter({ ...p.data, insight: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              {p.data.metrics.map((m, i) => {
                const mz = getZone(m.score);
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-3 rounded-lg bg-[var(--cream)]/50 border border-border/50">
                    <Input
                      className="col-span-12 md:col-span-4"
                      placeholder="Nombre métrica"
                      value={m.label}
                      onChange={(e) => p.ops.update(i, { label: e.target.value })}
                    />
                    <Input
                      className="col-span-6 md:col-span-2"
                      placeholder="Valor"
                      value={m.value}
                      onChange={(e) => p.ops.update(i, { value: e.target.value })}
                    />
                    <Input
                      className="col-span-6 md:col-span-2"
                      placeholder="Objetivo"
                      value={m.target || ""}
                      onChange={(e) => p.ops.update(i, { target: e.target.value })}
                    />
                    <div className="col-span-10 md:col-span-3 flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={m.score}
                        onChange={(e) => p.ops.update(i, { score: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                      />
                      <span className="text-xs font-mono shrink-0" style={{ color: zoneColor[mz] }}>
                        {zoneLabel[mz]}
                      </span>
                    </div>
                    <button
                      className="col-span-2 md:col-span-1 text-red-600 hover:text-red-800"
                      onClick={() => p.ops.remove(i)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}

        {/* Swing sequence */}
        <Card className="p-6 bg-white border border-[var(--forest-deep)]/10 mb-4">
          <h3 className="font-display text-xl font-medium text-[var(--forest-deep)] mb-4">
            Secuencia del swing
          </h3>
          <div className="space-y-2">
            {swingSeq.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input
                  className="col-span-4 md:col-span-2"
                  placeholder="Fase"
                  value={s.phase}
                  onChange={(e) => setSwingSeq(swingSeq.map((x, idx) => (idx === i ? { ...x, phase: e.target.value } : x)))}
                />
                <Input
                  className="col-span-3 md:col-span-2"
                  placeholder="Timing"
                  value={s.timing}
                  onChange={(e) => setSwingSeq(swingSeq.map((x, idx) => (idx === i ? { ...x, timing: e.target.value } : x)))}
                />
                <Input
                  className="col-span-3 md:col-span-2"
                  type="number"
                  min={0}
                  max={100}
                  value={s.quality}
                  onChange={(e) =>
                    setSwingSeq(
                      swingSeq.map((x, idx) =>
                        idx === i ? { ...x, quality: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } : x,
                      ),
                    )
                  }
                />
                <Input
                  className="col-span-12 md:col-span-6"
                  placeholder="Nota"
                  value={s.note || ""}
                  onChange={(e) => setSwingSeq(swingSeq.map((x, idx) => (idx === i ? { ...x, note: e.target.value } : x)))}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Strengths and alerts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <StringListCard title="Puntos fuertes" items={strengths} onChange={setStrengths} />
          <StringListCard title="Aspectos a mejorar" items={alerts} onChange={setAlerts} />
        </div>

        {/* Priorities */}
        <Card className="p-6 bg-white border border-[var(--forest-deep)]/10 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-medium text-[var(--forest-deep)]">Prioridades</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPriorities([
                  ...priorities,
                  { rank: priorities.length + 1, pillar: "", title: "", why: "", how: "" },
                ])
              }
            >
              + Prioridad
            </Button>
          </div>
          <div className="space-y-3">
            {priorities.map((p, i) => (
              <div key={i} className="p-4 rounded-lg bg-[var(--cream)]/50 border border-border/50 space-y-2">
                <div className="grid grid-cols-12 gap-2">
                  <Input className="col-span-2" type="number" value={p.rank} onChange={(e) => setPriorities(priorities.map((x, idx) => idx === i ? { ...x, rank: Number(e.target.value) } : x))} />
                  <Input className="col-span-4" placeholder="Pilar" value={p.pillar} onChange={(e) => setPriorities(priorities.map((x, idx) => idx === i ? { ...x, pillar: e.target.value } : x))} />
                  <Input className="col-span-6" placeholder="Título" value={p.title} onChange={(e) => setPriorities(priorities.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
                </div>
                <Textarea rows={2} placeholder="Por qué" value={p.why} onChange={(e) => setPriorities(priorities.map((x, idx) => idx === i ? { ...x, why: e.target.value } : x))} />
                <Textarea rows={2} placeholder="Cómo se entrena" value={p.how} onChange={(e) => setPriorities(priorities.map((x, idx) => idx === i ? { ...x, how: e.target.value } : x))} />
                <button className="text-red-600 text-xs" onClick={() => setPriorities(priorities.filter((_, idx) => idx !== i))}>
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* Save bar */}
        <div className="sticky bottom-4 bg-white border border-[var(--forest-deep)]/15 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg">
          <div className="text-xs text-[var(--forest-deep)]/70">
            {editing?.status === "published" ? "Visible para el jugador" : "En borrador"}
          </div>
          <div className="flex items-center gap-2">
            {editingId && (
              <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-300 hover:bg-red-50">
                <Trash2 size={16} className="mr-1" /> Eliminar
              </Button>
            )}
            <Button variant="outline" onClick={() => save("draft")}>
              <Save size={16} className="mr-1" /> Guardar borrador
            </Button>
            <Button onClick={() => save("published")} className="bg-[var(--forest)] text-[var(--cream)]">
              <Send size={16} className="mr-1" /> Publicar
            </Button>
          </div>
        </div>
      </div>
    </PrivateLayout>
  );
}

function StringListCard({ title, items, onChange }: { title: string; items: string[]; onChange: (v: string[]) => void }) {
  return (
    <Card className="p-5 bg-white border border-[var(--forest-deep)]/10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-medium text-[var(--forest-deep)]">{title}</h3>
        <Button variant="outline" size="sm" onClick={() => onChange([...items, ""])}>+</Button>
      </div>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={i} className="flex gap-2">
            <Input value={s} onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))} />
            <button className="text-red-600" onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}
